/**
 * Trader navigation grouped the way the reference app groups it: four
 * "main" destinations with icon tiles, then labelled sections underneath.
 *
 * The flat `traderNav` list stays the source of truth for which routes exist
 * and which the tenant has enabled — this file only decides *grouping*, and
 * every entry is filtered against the enabled list before it renders.
 */
import {
  LayoutDashboard, Wallet, TrendingUp, Gift, User, Banknote, ArrowLeftRight,
  Copy, Network, ShieldCheck, ArrowDown, ArrowUpRight, Star, History,
  Settings, MessagesSquare, Users, Receipt, Trophy, GitBranch, Layers, ArrowLeft, Share2, Award, ListOrdered,
  type LucideIcon,
} from "lucide-react";

export interface RdfxNavItem {
  /** Route path. Also the React key, so it stays unique for external items. */
  to: string;
  label: string;
  icon: LucideIcon;
  /**
   * External destination. When set the entry opens this in a new tab instead of
   * routing, and is never matched against the enabled-route list — it isn't a
   * route. Used for the broker's Telegram group.
   */
  href?: string;
}

export interface RdfxNavSection {
  title: string;
  items: RdfxNavItem[];
}

/** Top four — rendered with the large icon tiles. */
export const RDFX_MAIN_NAV: RdfxNavItem[] = [
  { to: "/trader/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/trader/accounts", label: "Accounts", icon: Wallet },
  { to: "/trader/web-trader", label: "Trade", icon: TrendingUp },
  { to: "/trader/rewards", label: "Rewards", icon: Gift },
];

export const RDFX_SECTIONS: RdfxNavSection[] = [
  {
    title: "Finances",
    items: [
      { to: "/trader/deposit", label: "Deposit", icon: ArrowDown },
      { to: "/trader/withdraw", label: "Withdraw", icon: ArrowUpRight },
      { to: "/trader/fund-transfer", label: "Fund Transfer", icon: ArrowLeftRight },
      { to: "/trader/banks", label: "Bank Accounts", icon: Banknote },
    ],
  },
  {
    title: "Trading",
    items: [
      { to: "/trader/copy-trading", label: "Copy Trading", icon: Copy },
      { to: "/trader/contest", label: "Contests", icon: Award },
      { to: "/trader/contest/leaderboard", label: "Leaderboard", icon: ListOrdered },
      { to: "/trader/contest/winners", label: "Winners", icon: Trophy },
      { to: "/trader/bonus", label: "Trader Bonuses", icon: Star },
      { to: "/trader/history", label: "Trade History", icon: History },
    ],
  },
  {
    title: "IB & Referrals",
    items: [
      { to: "/trader/become-ib", label: "IB Portal", icon: Network },
    ],
  },
  {
    title: "Account",
    items: [
      { to: "/trader/kyc", label: "KYC Verification", icon: ShieldCheck },
      { to: "/trader/profile", label: "Profile", icon: User },
      { to: "/trader/account-settings", label: "Account Settings", icon: Settings },
      { to: "/trader/support", label: "Support Tickets", icon: MessagesSquare },
    ],
  },
];

/** Breadcrumb label per route — falls back to the brand name. */
export const RDFX_PAGE_LABELS: Record<string, string> = Object.fromEntries(
  [...RDFX_MAIN_NAV, ...RDFX_SECTIONS.flatMap((s) => s.items)].map((i) => [i.to, i.label]),
);

/* ── IB portal ─────────────────────────────────────────────────────────── */

export const IB_MAIN_NAV: RdfxNavItem[] = [
  { to: "/ib/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/ib/clients", label: "My Clients", icon: Users },
  { to: "/ib/commissions", label: "Commissions", icon: Receipt },
  { to: "/ib/rewards", label: "IB Rewards", icon: Trophy },
];

export const IB_SECTIONS: RdfxNavSection[] = [
  {
    title: "Network",
    items: [
      { to: "/ib/sub-ibs", label: "My IBs", icon: Network },
      { to: "/ib/tree", label: "IB Tree", icon: GitBranch },
      { to: "/ib/referrals", label: "Referral Links", icon: Share2 },
    ],
  },
  {
    title: "Earnings",
    items: [
      { to: "/ib/allocate", label: "Allocate Commission", icon: Layers },
      { to: "/ib/withdraw", label: "Transfer to Wallet", icon: Banknote },
    ],
  },
  {
    title: "Account",
    items: [
      { to: "/trader/dashboard", label: "Back to Trader Area", icon: ArrowLeft },
    ],
  },
];

export const IB_PAGE_LABELS: Record<string, string> = Object.fromEntries(
  [...IB_MAIN_NAV, ...IB_SECTIONS.flatMap((s) => s.items)].map((i) => [i.to, i.label]),
);
